/**
 * CMS Carousel Composable
 *
 * Parses HTML content with carousel placeholders and fetches products.
 *
 * Usage in CMS content:
 *
 * 1. By category/filters:
 *    <div data-carousel="categories=seasonal&pageSize=12"></div>
 *
 * 2. By specific itemIds (case-insensitive):
 *    <div data-carousel="ID=166229&ID=167314&ID=168901"></div>
 *    <div data-carousel="id=166229&id=167314"></div>
 *
 * Optional: Add CSS classes for styling:
 *    <div class="my-custom-class" data-carousel="ID=166229&ID=167314"></div>
 */

import type { ProductAPIResponse, QueryProductsAPIResponse } from '~/types/shop'
import { mapQueryParams } from '~/utils/shop'
import { cmsCarouselPlaceholders } from '~/utils/cmsContent'
import { toError } from '~/utils/http/api-error'

interface ContentSection {
  type: 'html' | 'carousel'
  content?: string
  queryString?: string
  products?: ProductAPIResponse[]
  loading?: boolean
  className?: string
}

export const useCmsCarousel = () => {
  const { $appInsights, $shopService } = useNuxtApp()

  const contentSections = ref<ContentSection[]>([])
  const hasCarousels = ref(false)

  /**
   * Parse HTML content and extract carousel placeholders
   * Looks for <div data-carousel="queryString"> tags and splits content into sections
   */
  const parseContent = (html: string): ContentSection[] => {
    const sections: ContentSection[] = []
    let lastIndex = 0
    let foundCarousels = false

    for (const match of cmsCarouselPlaceholders(html)) {
      foundCarousels = true

      // Add HTML content before the carousel
      if (match.index > lastIndex) {
        sections.push({
          type: 'html',
          content: html.substring(lastIndex, match.index)
        })
      }

      // Extract class from attributes (match[1] contains all attributes before data-carousel)
      const classMatch = match[1] != null ? match[1].match(/class="([^"]*)"/) : null
      const className = classMatch?.[1] ?? undefined

      // Add carousel placeholder with extracted class
      sections.push({
        type: 'carousel',
        queryString: match[2],
        products: [],
        loading: true,
        className
      })

      lastIndex = match.index + match[0].length
    }

    // Add remaining HTML after last carousel
    if (lastIndex < html.length) {
      sections.push({
        type: 'html',
        content: html.substring(lastIndex)
      })
    }

    hasCarousels.value = foundCarousels
    return sections
  }

  /**
   * Fetch products for a carousel based on query string
   * Supports two modes:
   * 1. By itemIds: data-carousel="ID=123&ID=456&ID=789"
   * 2. By category/filters: data-carousel="categories=seasonal&pageSize=12"
   */
  const fetchCarouselProducts = async (queryString: string): Promise<ProductAPIResponse[]> => {
    try {
      // Guard against undefined or empty queryString
      if (!queryString) {
        return []
      }

      // Parse the query parameters
      const params = new URLSearchParams(queryString)

      // Check if this is an itemId-based query (has ID parameter, case-insensitive)
      const itemIds = [...params.getAll('ID'), ...params.getAll('id')]

      if (itemIds.length > 0) {
        // Build query string properly using URLSearchParams.append (same as useProductRelationships)
        const productParams = new URLSearchParams()
        itemIds.forEach(id => productParams.append('ID', id))
        const productQuery = productParams.toString()

        const endpoint = `products?${productQuery}`
        const { products } = await $api()<QueryProductsAPIResponse>(endpoint)
        return products || []
      }

      // Otherwise, use the category/filter-based approach
      const queryObject: Record<string, string | number> = {}

      // Convert URLSearchParams to object
      params.forEach((value, key) => {
        // Convert numeric strings to numbers
        const numValue = Number(value)
        queryObject[key] = !isNaN(numValue) && value.trim() !== '' ? numValue : value
      })

      // Set default pageSize if not specified
      if (!queryObject.pageSize) {
        queryObject.pageSize = 12
      }

      // Set default page if not specified
      if (!queryObject.page) {
        queryObject.page = 1
      }

      // Use mapQueryParams to transform to API format (handles filter mappings like colors -> suColors)
      const apiParams = mapQueryParams(queryObject)

      // Fetch products using the shop service
      const response = await $shopService.fetchProductList(apiParams, false, undefined)

      return response.products || []
    } catch (error) {
      $appInsights?.trackException({
        exception: toError(error),
        properties: { queryString }
      })
      return []
    }
  }

  /**
   * Parse HTML content into `contentSections`, then fetch the products for its
   * carousels.
   *
   * The sections are published before the fetch, with each carousel still
   * `loading`, so they change in the same tick as `hasCarousels` does. Publishing
   * them only once the products had arrived left the previous payload's sections
   * on screen under the new page until then, and let a slower earlier fetch
   * overwrite a later payload.
   *
   * The products are written through the reactive sections, so each carousel
   * fills in as its own fetch resolves. A fetch that outlives its payload writes
   * to sections that are no longer published.
   */
  const parseAndFetchContent = async (html: string | false): Promise<ContentSection[]> => {
    if (!html || typeof html !== 'string') {
      hasCarousels.value = false
      contentSections.value = []
      return []
    }

    contentSections.value = parseContent(html)
    const sections = contentSections.value

    // Fetch all carousel products in parallel
    await Promise.all(
      sections.filter(s => s.type === 'carousel').map(async (section) => {
        if (section.queryString) {
          section.products = await fetchCarouselProducts(section.queryString)
          section.loading = false
        }
      })
    )

    return sections
  }

  return {
    contentSections,
    hasCarousels,
    parseContent,
    fetchCarouselProducts,
    parseAndFetchContent
  }
}
